import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { Spinner, EmptyState } from "@/components/common";
import { ListingCard } from "@/components/ListingCard";
import { PersonCard } from "@/components/PersonCard";
import { TribeCard } from "@/components/TribeCard";
import { CITIES, OPTIONS, CONFIGS, DISTANCES, fmtRent } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { SlidersHorizontal, DoorOpen, Users, Home as HomeIcon, Bookmark } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "rooms", label: "A Room", icon: DoorOpen, desc: "Move into an existing flat and find a suitable room." },
  { id: "people", label: "A Flatmate", icon: Users, desc: "Have a flat or room? Find a compatible person to share it." },
  { id: "tribes", label: "A Tribe", icon: HomeIcon, desc: "Find compatible people first, then search for a flat together." },
];

export default function Discover() {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "rooms");
  const [loading, setLoading] = useState(true);
  const [rooms, setRooms] = useState([]);
  const [people, setPeople] = useState([]);
  const [tribes, setTribes] = useState([]);
  const [savedIds, setSavedIds] = useState([]);
  const [filters, setFilters] = useState({ city: "", room_type: "", min_rent: 5000, max_rent: 60000, sort: "compatibility", min_age: 18, max_age: 60, age_on: false, max_distance_km: 0, areas: [], food_arrangement: "" });
  const [open, setOpen] = useState(false);
  const [areaInput, setAreaInput] = useState("");
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");

  useEffect(() => {
    api.get("/saved/ids").then((r) => setSavedIds(r.data.ids)).catch(() => {});
    api.get("/presets").then((r) => setPresets(r.data)).catch(() => {});
  }, []);

  const savePreset = async () => {
    const name = presetName.trim() || `${filters.city || "Any city"} · ${tab}`;
    try {
      const { data } = await api.post("/presets", { name, tab, filters });
      setPresets((s) => [data, ...s]);
      setPresetName("");
      toast.success("Search saved");
    } catch { toast.error("Could not save search"); }
  };
  const applyPreset = (p) => {
    setTab(p.tab); setParams({ tab: p.tab });
    setFilters({ city: "", room_type: "", min_rent: 5000, max_rent: 60000, sort: "compatibility", min_age: 18, max_age: 60, age_on: false, max_distance_km: 0, areas: [], food_arrangement: "", ...p.filters });
    setOpen(false);
  };
  const deletePreset = async (pid) => {
    await api.delete(`/presets/${pid}`);
    setPresets((s) => s.filter((x) => x.preset_id !== pid));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "rooms") {
        const q = { sort: filters.sort };
        if (filters.city) q.city = filters.city;
        if (filters.room_type) q.room_type = filters.room_type;
        q.min_rent = filters.min_rent; q.max_rent = filters.max_rent;
        if (filters.areas.length) q.areas = filters.areas.join(",");
        if (filters.food_arrangement) q.food_arrangement = filters.food_arrangement;
        if (filters.max_distance_km) q.max_distance_km = filters.max_distance_km;
        const { data } = await api.get("/listings", { params: q });
        setRooms(data);
      } else if (tab === "people") {
        const q = {};
        if (filters.city) q.city = filters.city;
        if (filters.age_on) { q.min_age = filters.min_age; q.max_age = filters.max_age; }
        if (filters.areas.length) q.areas = filters.areas.join(",");
        if (filters.max_distance_km) q.max_distance_km = filters.max_distance_km;
        const { data } = await api.get("/people", { params: q });
        setPeople(data);
      } else {
        const { data } = await api.get("/tribes", { params: filters.city ? { city: filters.city } : {} });
        setTribes(data);
      }
    } finally { setLoading(false); }
  }, [tab, filters]);

  useEffect(() => { load(); }, [load]);

  const setTabAndUrl = (t) => { setTab(t); setParams({ tab: t }); };
  const onSave = (id, on) => setSavedIds((s) => (on ? [...s, id] : s.filter((x) => x !== id)));

  return (
    <div>
      <TopBar greeting="Explore" name="Discover" />
      <div className="sticky top-[73px] z-20 bg-background/95 px-4 py-3 backdrop-blur-xl">
        <div className="flex items-center gap-2">
          <div className="flex flex-1 rounded-full bg-secondary p-1">
            {TABS.map((t) => (
              <button key={t.id} data-testid={`discover-tab-${t.id}`} onClick={() => setTabAndUrl(t.id)}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-full py-2 text-sm font-bold transition-colors ${tab === t.id ? "bg-white text-ink shadow-soft" : "text-ink-soft"}`}>
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            ))}
          </div>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="open-filters" className="grid h-11 w-11 place-items-center rounded-full bg-ink text-white"><SlidersHorizontal className="h-5 w-5" /></button>
            </SheetTrigger>
            <SheetContent side="bottom" className="mx-auto max-w-[480px] rounded-t-3xl">
              <SheetHeader><SheetTitle className="font-heading text-2xl font-extrabold">Filters</SheetTitle><SheetDescription className="text-ink-soft">Refine rooms, people and tribes.</SheetDescription></SheetHeader>
              <div className="space-y-5 py-4">
                <div className="rounded-2xl bg-secondary p-3">
                  <p className="mb-2 text-sm font-semibold text-ink">Save this search</p>
                  <div className="flex gap-2">
                    <Input data-testid="preset-name-input" value={presetName} onChange={(e) => setPresetName(e.target.value)} placeholder="e.g. HSR under 20k" className="h-10 rounded-2xl bg-white" />
                    <Button data-testid="save-preset" onClick={savePreset} className="h-10 rounded-2xl bg-ink px-4 text-white">Save</Button>
                  </div>
                </div>
                <div>
                  <p className="mb-2 text-sm font-semibold text-ink">City</p>
                  <div className="flex flex-wrap gap-2">
                    <FilterChip active={!filters.city} onClick={() => setFilters({ ...filters, city: "" })}>All</FilterChip>
                    {CITIES.slice(0, 5).map((c) => <FilterChip key={c} active={filters.city === c} onClick={() => setFilters({ ...filters, city: c })}>{c}</FilterChip>)}
                  </div>
                </div>
                {tab === "rooms" && (
                  <>
                    <div>
                      <p className="mb-2 text-sm font-semibold text-ink">Room type</p>
                      <div className="flex flex-wrap gap-2">
                        <FilterChip active={!filters.room_type} onClick={() => setFilters({ ...filters, room_type: "" })}>Any</FilterChip>
                        {OPTIONS.room_preference.filter((o) => o.v !== "any").map((o) => <FilterChip key={o.v} active={filters.room_type === o.v} onClick={() => setFilters({ ...filters, room_type: o.v })}>{o.l}</FilterChip>)}
                      </div>
                    </div>
                    <div>
                      <p className="mb-2 text-sm font-semibold text-ink">Max rent — {fmtRent(filters.max_rent)}</p>
                      <Slider value={[filters.max_rent]} min={5000} max={80000} step={1000} onValueChange={(v) => setFilters({ ...filters, max_rent: v[0] })} />
                    </div>
                    <div>
                      <p className="mb-2 text-sm font-semibold text-ink">Food arrangement</p>
                      <div className="flex flex-wrap gap-2">
                        <FilterChip active={!filters.food_arrangement} onClick={() => setFilters({ ...filters, food_arrangement: "" })}>No preference</FilterChip>
                        {OPTIONS.food_arrangement.map((o) => <FilterChip key={o.v} active={filters.food_arrangement === o.v} onClick={() => setFilters({ ...filters, food_arrangement: o.v })}>{o.l}</FilterChip>)}
                      </div>
                    </div>
                    <div>
                      <p className="mb-2 text-sm font-semibold text-ink">Sort by</p>
                      <div className="flex flex-wrap gap-2">
                        {[["compatibility", "Best match"], ["rent_low", "Rent: low"], ["rent_high", "Rent: high"]].map(([v, l]) => (
                          <FilterChip key={v} active={filters.sort === v} onClick={() => setFilters({ ...filters, sort: v })}>{l}</FilterChip>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                {tab === "people" && (
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <p className="text-sm font-semibold text-ink">Age range {filters.age_on ? `— ${filters.min_age}–${filters.max_age}` : ""}</p>
                      <label className="flex items-center gap-2 text-xs text-ink-soft">Filter age
                        <input data-testid="filter-age-toggle" type="checkbox" checked={filters.age_on} onChange={(e) => setFilters({ ...filters, age_on: e.target.checked })} className="h-4 w-4 accent-brand" />
                      </label>
                    </div>
                    {filters.age_on && (
                      <div className="space-y-3 rounded-2xl bg-white p-3">
                        <div><span className="text-xs text-ink-soft">Min age</span><Slider value={[filters.min_age]} min={18} max={60} step={1} onValueChange={(v) => setFilters({ ...filters, min_age: Math.min(v[0], filters.max_age) })} /></div>
                        <div><span className="text-xs text-ink-soft">Max age</span><Slider value={[filters.max_age]} min={18} max={60} step={1} onValueChange={(v) => setFilters({ ...filters, max_age: Math.max(v[0], filters.min_age) })} /></div>
                      </div>
                    )}
                  </div>
                )}
                {tab !== "tribes" && (
                  <>
                    <div>
                      <p className="mb-2 text-sm font-semibold text-ink">Distance from your areas</p>
                      <div className="flex flex-wrap gap-2">
                        {DISTANCES.map((d) => <FilterChip key={d.v} active={filters.max_distance_km === d.v} onClick={() => setFilters({ ...filters, max_distance_km: d.v })}>{d.l}</FilterChip>)}
                      </div>
                    </div>
                    <div>
                      <p className="mb-2 text-sm font-semibold text-ink">Preferred areas</p>
                      <div className="flex gap-2">
                        <Input data-testid="filter-area-input" value={areaInput} onChange={(e) => setAreaInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); const v = areaInput.trim(); if (v && !filters.areas.includes(v)) setFilters({ ...filters, areas: [...filters.areas, v] }); setAreaInput(""); } }}
                          placeholder="e.g. Koramangala" className="h-10 rounded-2xl bg-white" />
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {filters.areas.map((a) => <span key={a} onClick={() => setFilters({ ...filters, areas: filters.areas.filter((x) => x !== a) })} className="cursor-pointer rounded-full bg-brand-light px-3 py-1 text-sm font-semibold text-brand-dark">{a} ✕</span>)}
                      </div>
                    </div>
                  </>
                )}
                <Button data-testid="apply-filters" onClick={() => setOpen(false)} className="h-12 w-full rounded-full bg-brand font-bold text-white">Show results</Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <div className="px-4 pt-2">
        <div className="mb-3 flex items-center gap-2 rounded-2xl bg-brand-light px-3 py-2 text-sm font-medium text-brand-dark" data-testid="discover-desc">
          <span className="font-bold">Looking for {TABS.find((t) => t.id === tab)?.label}:</span> {TABS.find((t) => t.id === tab)?.desc}
        </div>
        {presets.length > 0 && (
          <div className="no-scrollbar mb-3 flex gap-2 overflow-x-auto pb-1" data-testid="preset-rail">
            {presets.map((p) => (
              <div key={p.preset_id} data-testid={`preset-${p.preset_id}`} className="flex shrink-0 items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-ink shadow-soft">
                <button onClick={() => applyPreset(p)} className="flex items-center gap-1.5"><Bookmark className="h-3.5 w-3.5 text-brand" /> {p.name}</button>
                <button data-testid={`delete-preset-${p.preset_id}`} onClick={() => deletePreset(p.preset_id)} className="text-ink-soft/60 hover:text-destructive">✕</button>
              </div>
            ))}
          </div>
        )}
        {loading ? <Spinner /> : (
          <>
            {tab === "rooms" && (rooms.length ? <div className="space-y-5">{rooms.map((l) => <ListingCard key={l.listing_id} listing={l} savedIds={savedIds} onSaveChange={onSave} />)}</div>
              : <EmptyState icon={DoorOpen} title="No rooms found" sub="Try widening your filters." />)}
            {tab === "people" && (people.length ? <div className="grid grid-cols-2 gap-4">{people.map((p) => <PersonCard key={p.user_id} person={p} savedIds={savedIds} onSaveChange={onSave} />)}</div>
              : <EmptyState icon={Users} title="No people yet" sub="Check back soon." />)}
            {tab === "tribes" && (tribes.length ? <div className="space-y-4">{tribes.map((t) => <TribeCard key={t.tribe_id} tribe={t} />)}</div>
              : <EmptyState icon={HomeIcon} title="No tribes yet" sub="Start one from the Post tab!" />)}
          </>
        )}
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, children }) {
  return <button onClick={onClick} className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all ${active ? "border-brand bg-brand text-white" : "border-border bg-white text-ink-soft"}`}>{children}</button>;
}
